import { useCallback, useState } from 'react';
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Page,
  Text,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { PLANS, type PlanKey, planInfo } from '../plans';

const FULL_TRIAL_DAYS = 7;

const FEATURES = [
  'Signing requests sent automatically when orders are placed',
  'Unlimited contract templates — upload a PDF or write your own',
  'Tamper-proof signed PDFs with a Certificate of Completion',
  'Customers sign on any device, no account needed',
];

/** Statuses that mean the merchant subscribed before and has since stopped. */
const RETURNING = ['CANCELLED', 'EXPIRED', 'FROZEN'];

/**
 * Shown instead of the dashboard whenever the store has no active
 * subscription (the API answered 402). Explains the plan before sending the
 * merchant to Shopify's charge approval page.
 */
export default function WelcomePage({
  subscriptionStatus,
  trialDaysRemaining,
}: {
  subscriptionStatus: string;
  trialDaysRemaining: number;
}) {
  const { startSubscription, request } = useApi();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<PlanKey>('PRO');
  const price = planInfo(plan).price;

  // A store that has used part of its trial is returning even after a reinstall.
  const returning = RETURNING.includes(subscriptionStatus) || trialDaysRemaining < FULL_TRIAL_DAYS;
  const days = trialDaysRemaining;
  const trialEnds = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toLocaleDateString(
    'en-US',
    { month: 'long', day: 'numeric' },
  );

  const trialTitle =
    days === 0
      ? 'Your free trial has already been used'
      : days === FULL_TRIAL_DAYS
        ? `${days}-day free trial — you won't be charged today`
        : `${days} ${days === 1 ? 'day' : 'days'} left of your free trial — you won't be charged today`;
  const trialBody =
    days === 0
      ? `Billing of $${price}/month starts when you approve the subscription, through your regular Shopify bill. Cancel anytime from Settings.`
      : `Billing of $${price}/month starts on ${trialEnds}, through your regular Shopify bill. Cancel anytime from Settings — cancel during the trial and you pay nothing.`;
  const buttonLabel =
    days === 0
      ? `Subscribe — $${price}/month`
      : days === FULL_TRIAL_DAYS
        ? `Start ${days}-day free trial`
        : 'Continue free trial';

  const start = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      await startSubscription(plan);
      // Navigating to Shopify's approval page — keep the button spinning.
    } catch (e: any) {
      setError(e.message ?? 'Could not start your subscription. Please try again.');
      setStarting(false);
    }
  }, [startSubscription, plan]);

  // Re-reads the subscription from Shopify (never creates a charge), then reloads.
  const checkAgain = useCallback(async () => {
    setStarting(true);
    await request('/auth/billing-refresh').catch(() => {});
    window.location.reload();
  }, [request]);

  // Shopify froze the store (unpaid Shopify bill, ended Shopify trial, paused
  // store…). It rejects new charges until that's fixed, so offering "Subscribe"
  // would only fail. Unfreezing reactivates SignShield via app_subscriptions/update.
  if (subscriptionStatus === 'FROZEN') {
    return (
      <Page narrowWidth>
        <BlockStack gap="500">
          <BlockStack gap="200" inlineAlign="center">
            <img src="/logo-icon.png" alt="" style={{ width: 56, height: 56 }} />
            <Text as="h1" variant="headingXl" alignment="center">
              SignShield is on hold
            </Text>
          </BlockStack>

          <Card>
            <BlockStack gap="400">
              <Banner tone="warning" title="Your Shopify store is currently paused or frozen">
                <p>
                  Shopify has put your store's app subscriptions on hold. This usually happens when
                  a Shopify plan hasn't been chosen after the Shopify trial, a Shopify bill is unpaid,
                  or the store has been paused.
                </p>
              </Banner>
              <Text as="p">
                Once you resolve it in your Shopify billing settings, SignShield will reactivate
                automatically — your templates, signed documents and settings are all kept.
              </Text>
              <InlineStack gap="300">
                <Button variant="primary" onClick={() => window.open('shopify://admin/settings/billing', '_top')}>
                  Open Shopify billing settings
                </Button>
                <Button onClick={checkAgain} loading={starting}>Check again</Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Text as="p" variant="bodySm" tone="subdued" alignment="center">
            Questions? Email <a href="mailto:support@merchantkits.com">support@merchantkits.com</a>
          </Text>
        </BlockStack>
      </Page>
    );
  }

  return (
    <Page narrowWidth>
      <BlockStack gap="500">
        <BlockStack gap="200" inlineAlign="center">
          <img src="/logo-icon.png" alt="" style={{ width: 56, height: 56 }} />
          <Text as="h1" variant="headingXl" alignment="center">
            {returning ? 'Welcome back to SignShield' : 'Welcome to SignShield'}
          </Text>
          <Text as="p" tone="subdued" alignment="center">
            Collect legally binding e-signatures on your Shopify orders — automatically.
          </Text>
        </BlockStack>

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}

        <Card>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">Choose your plan</Text>
              <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
                {PLANS.map((p) => {
                  const selected = p.key === plan;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPlan(p.key)}
                      aria-pressed={selected}
                      style={{
                        textAlign: 'left', cursor: 'pointer', background: selected ? '#f1f8f5' : '#fff',
                        border: selected ? '2px solid #29845a' : '1px solid #d4d4d4', borderRadius: 12,
                        padding: selected ? 13 : 14, font: 'inherit',
                      }}
                    >
                      <BlockStack gap="100">
                        <Text as="span" fontWeight="semibold">{p.name}</Text>
                        <InlineStack gap="100" blockAlign="baseline" wrap={false}>
                          <Text as="span" variant="headingLg">${p.price}</Text>
                          <Text as="span" tone="subdued">/ month</Text>
                        </InlineStack>
                        <Text as="span" variant="bodySm"><strong>{p.monthlyLimit}</strong> signing requests per month</Text>
                        <Text as="span" variant="bodySm" tone={p.multiSigner ? undefined : 'subdued'}>
                          {p.multiSigner ? '✓ Up to 3 signers per document' : 'One signer per document'}
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">{p.hint}</Text>
                      </BlockStack>
                    </button>
                  );
                })}
              </InlineGrid>
            </BlockStack>

            <Divider />

            <BlockStack gap="200">
              {FEATURES.map((f) => (
                <InlineStack key={f} gap="200" blockAlign="start" wrap={false}>
                  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" style={{ flexShrink: 0 }}>
                    <path d="M5 10.5l3 3 7-7" fill="none" stroke="#29845a" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <Text as="span">{f}</Text>
                </InlineStack>
              ))}
            </BlockStack>

            <Box
              background={days > 0 ? 'bg-surface-success' : 'bg-surface-secondary'}
              padding="300"
              borderRadius="200"
            >
              <BlockStack gap="100">
                <Text as="p" fontWeight="semibold">{trialTitle}</Text>
                <Text as="p" variant="bodySm">{trialBody}</Text>
              </BlockStack>
            </Box>

            <Button variant="primary" size="large" fullWidth onClick={start} loading={starting}>
              {buttonLabel}
            </Button>
            <Text as="p" variant="bodySm" tone="subdued" alignment="center">
              Next, Shopify will ask you to approve the subscription.
            </Text>
          </BlockStack>
        </Card>

        <Text as="p" variant="bodySm" tone="subdued" alignment="center">
          Questions? Email <a href="mailto:support@merchantkits.com">support@merchantkits.com</a>
        </Text>
      </BlockStack>
    </Page>
  );
}
